import React, { useContext, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
} from '@dls/react-core';
import { deleteSubjectData, MeasurementType, Subject } from '../../api/studykit';
import { StudyPageContext } from '../../App';
import {
  StyledDialogHeading,
  StyledDialogIconContainer,
  StyledDialogTitleForIcon,
} from '../styled/styles';
import { ColorX11 } from '../../styling/color';
import { useSnackbar } from 'notistack';
import { Icon } from '@dls/react-icon';

interface Props {
  open: boolean;
  onClose: () => void;
  subject: Subject;
  measurements?: MeasurementType[];
}

export const DeleteDataConfirmDialog: React.FC<Props> = ({
  open,
  onClose,
  subject,
  measurements,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { study } = useContext(StudyPageContext);
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteButtonClick = async () => {
    try {
      setIsSubmitting(true);
      await deleteSubjectData(
        study.id,
        subject.id,
        measurements?.map((m) => m.name),
      );
      enqueueSnackbar('Data successfully deleted', { variant: 'success' });
      onClose();
    } catch (e) {
      enqueueSnackbar('Could not delete data', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ style: { width: `50vw`, maxWidth: `600px` } }}
    >
      <StyledDialogTitleForIcon disableTypography>
        <StyledDialogIconContainer>
          <Icon name={'DeleteTrash'} size={'m'} color={ColorX11.Black} />
        </StyledDialogIconContainer>
        <StyledDialogHeading variant={'h3'}>Delete {subject.name}'s data</StyledDialogHeading>
      </StyledDialogTitleForIcon>
      <DialogContent>
        <DialogContentText>Are you sure you want to delete the following data?</DialogContentText>
        {measurements ? (
          <>
            <ul>
              {measurements.map((m) => (
                <li key={m.name}>{m.name}</li>
              ))}
            </ul>
            <DialogContentText style={{ fontWeight: 600 }}>From {subject.name}</DialogContentText>
          </>
        ) : (
          <DialogContentText style={{ fontWeight: 600 }}>
            All {subject.name}'s data
          </DialogContentText>
        )}
      </DialogContent>
      <Divider orientation={'horizontal'} />
      <DialogActions style={{ justifyContent: `space-between` }}>
        <Button
          style={{ width: `8rem` }}
          disabled={isSubmitting}
          onClick={onClose}
          dls_variant={'quietDefault'}
        >
          Cancel
        </Button>
        <Button
          disabled={isSubmitting}
          onClick={handleDeleteButtonClick}
          dls_variant={'signalError'}
          startIcon={
            isSubmitting ? (
              <CircularProgress variant={'indeterminate'} dls_size={'small'} />
            ) : (
              <Icon name={'DeleteTrash'} size={'m'} />
            )
          }
        >
          I am sure
        </Button>
      </DialogActions>
    </Dialog>
  );
};
